import { Button } from "components/ui/button";
import { Calendar } from "components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { cn } from "lib/utils";
import { CalendarIcon } from "lucide-react";
import moment from "moment";
import { useState, useEffect, useRef } from "react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "components/ui/select";

export default function DatePicker({
  value,
  className,
  onChange,
  placeholder = "Pick a date",
  disabled = false,
}: {
  value: string; // Accept `value` as a string in "YYYY-MM-DD" format
  className?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [date, setDate] = useState<Date | undefined>(value ? moment(value, "YYYY-MM-DD").toDate() : undefined);
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
  const [selectedYear, setSelectedYear] = useState<number>(date ? moment(date).year() : moment().year());
  const [selectedMonth, setSelectedMonth] = useState<number>(date ? moment(date).month() : moment().month());
  const calendarRef = useRef<HTMLDivElement>(null);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 55 }, (_, i) => currentYear + 5 - i);
  const months = moment.months(); // Array of month names

  useEffect(() => {
    if (value) {
      setDate(moment(value, "YYYY-MM-DD").toDate());
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      if (calendarRef.current && !calendarRef.current.contains(target) && !target.closest("[data-datepicker-toggle]")) {
        // Close the calendar when clicking outside
        // setIsOpenDialog(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleYearChange = (year: string) => {
    const newYear = Number(year);
    setSelectedYear(newYear);

    // Create a new Date with new year + current month + 1st day
    const updatedDate = new Date(newYear, selectedMonth, 1);
  };

  const handleMonthChange = (month: string) => {
    const newMonth = moment().month(month).month();
    setSelectedMonth(newMonth);
  };

  return (
    <Popover open={isOpenDialog} onOpenChange={(open) => setIsOpenDialog(open)}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          onClick={() => {
            setIsOpenDialog((prev) => !prev);
          }}
          className={cn("w-full pl-3 text-left font-normal border dark:border-gray-800", className)}
          data-datepicker-toggle
          disabled={disabled}
        >
          {date ? <span className=" text-[15px]">{moment(date).format("DD-MMM-YYYY")}</span> : <span className="text-[#9ca3af] text-[15px]">{placeholder}</span>}

          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent ref={calendarRef} className="w-auto p-0" align="start">
        <div className="">
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-2 pt-2 px-2 ">
              {/* Month Selection */}
              <Select value={months[selectedMonth]} onValueChange={handleMonthChange}>
                <SelectTrigger onClick={() => setIsOpenDialog(true)} className="">
                  <SelectValue>{months[selectedMonth]}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {months.map((month, index) => (
                    <SelectItem key={month} value={month}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Year Selection */}
              <Select value={selectedYear.toString()} onValueChange={handleYearChange}>
                <SelectTrigger onClick={() => setIsOpenDialog(true)} className="">
                  <SelectValue>{selectedYear}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Calendar
              className="p-0 px-2 pb-2"
              mode="single"
              selected={date}
              month={moment().year(selectedYear).month(selectedMonth).toDate()}
              onMonthChange={(newMonth) => {
                setSelectedYear(moment(newMonth).year());
                setSelectedMonth(moment(newMonth).month());
              }}
              onSelect={(selectedDate) => {
                if (selectedDate) {
                  const formattedDate = moment(selectedDate).format("YYYY-MM-DD");
                  setDate(selectedDate);
                  onChange(formattedDate);
                  setIsOpenDialog(false);
                }
              }}
              disabled={disabled}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
